import {
  ArrowUpTrayIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';

function Magazine() {
  const [magazines, setMagazines] = useState([
    {
      id: 1,
      title: 'Winter 2024',
      quarter: 'Q1 2024',
      publishedAt: '2024-01-01',
      url: '#',
      pdfSize: '12.3 MB',
      pages: 48,
      uploadedBy: 'Admin',
      uploadedAt: '2023-12-15',
    },
    {
      id: 2,
      title: 'Autumn 2023',
      quarter: 'Q4 2023',
      publishedAt: '2023-10-01',
      url: '#',
      pdfSize: '10.8 MB',
      pages: 44,
      uploadedBy: 'Admin',
      uploadedAt: '2023-09-20',
    },
  ]);

  const [isUploading, setIsUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editMode, setEditMode] = useState(null);

  const [uploadForm, setUploadForm] = useState({
    title: '',
    quarter: '',
    publishedAt: '',
    file: null,
  });

  const handleFileSelect = e => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadForm({ ...uploadForm, file });
    } else {
      alert('Please select a PDF file');
    }
  };

  const handleUpload = async e => {
    e.preventDefault();

    if (!uploadForm.file || !uploadForm.title || !uploadForm.quarter || !uploadForm.publishedAt) {
      alert('Please fill all fields and select a PDF file');
      return;
    }

    setIsUploading(true);

    // Simulate upload delay
    setTimeout(() => {
      const newMagazine = {
        id: magazines.length + 1,
        title: uploadForm.title,
        quarter: uploadForm.quarter,
        publishedAt: uploadForm.publishedAt,
        url: '#',
        pdfSize: (uploadForm.file.size / (1024 * 1024)).toFixed(1) + ' MB',
        pages: 0, // Would be extracted from PDF in real implementation
        uploadedBy: 'Admin',
        uploadedAt: new Date().toISOString().split('T')[0],
      };

      setMagazines([newMagazine, ...magazines]);
      setIsUploading(false);
      setShowUploadModal(false);
      setUploadForm({ title: '', quarter: '', publishedAt: '', file: null });
    }, 2000);
  };

  const handleDelete = magazineId => {
    setMagazines(magazines.filter(m => m.id !== magazineId));
    setDeleteConfirm(null);
  };

  const handleEdit = (magazineId, updatedData) => {
    setMagazines(magazines.map(m => (m.id === magazineId ? { ...m, ...updatedData } : m)));
    setEditMode(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-hdlcc-textDark uppercase">Magazine Management</h2>
          <p className="text-hdlcc-textMuted">Upload and manage quarterly club magazines</p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 bg-hdlcc-accentRed text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
        >
          <ArrowUpTrayIcon className="w-5 h-5" />
          Upload Magazine
        </button>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-hdlcc-bgWhite rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-hdlcc-textDark mb-4">Upload New Magazine</h3>
            <form onSubmit={handleUpload}>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="magazine-title"
                    className="block text-sm font-medium text-hdlcc-textDark mb-1"
                  >
                    Magazine Title
                  </label>
                  <input
                    id="magazine-title"
                    type="text"
                    placeholder="e.g., Winter 2024"
                    value={uploadForm.title}
                    onChange={e => setUploadForm({ ...uploadForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-hdlcc-accentRed"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="magazine-quarter"
                    className="block text-sm font-medium text-hdlcc-textDark mb-1"
                  >
                    Quarter
                  </label>
                  <select
                    id="magazine-quarter"
                    value={uploadForm.quarter}
                    onChange={e => setUploadForm({ ...uploadForm, quarter: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-hdlcc-accentRed"
                    required
                  >
                    <option value="">Select Quarter</option>
                    <option value="Q1 2025">Q1 2025</option>
                    <option value="Q2 2025">Q2 2025</option>
                    <option value="Q3 2025">Q3 2025</option>
                    <option value="Q4 2025">Q4 2025</option>
                    <option value="Q1 2024">Q1 2024</option>
                    <option value="Q2 2024">Q2 2024</option>
                    <option value="Q3 2024">Q3 2024</option>
                    <option value="Q4 2024">Q4 2024</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="magazine-published-at"
                    className="block text-sm font-medium text-hdlcc-textDark mb-1"
                  >
                    Publication Date
                  </label>
                  <input
                    id="magazine-published-at"
                    type="date"
                    value={uploadForm.publishedAt}
                    onChange={e => setUploadForm({ ...uploadForm, publishedAt: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-hdlcc-accentRed"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="magazine-pdf"
                    className="block text-sm font-medium text-hdlcc-textDark mb-1"
                  >
                    PDF File
                  </label>
                  <input
                    id="magazine-pdf"
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-hdlcc-accentRed"
                    required
                  />
                  {uploadForm.file && (
                    <p className="text-sm text-hdlcc-textMuted mt-1">
                      Selected: {uploadForm.file.name} (
                      {(uploadForm.file.size / (1024 * 1024)).toFixed(1)} MB)
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadForm({ title: '', quarter: '', publishedAt: '', file: null });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-hdlcc-textDark transition-colors"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUploading}
                  className="px-4 py-2 bg-hdlcc-accentRed text-white rounded hover:bg-red-600 transition-colors disabled:bg-gray-400"
                >
                  {isUploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-hdlcc-bgWhite rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-hdlcc-textDark mb-4">Confirm Delete</h3>
            <p className="text-hdlcc-textMuted mb-6">
              Are you sure you want to delete &ldquo;{deleteConfirm.title}&rdquo;? This action
              cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-hdlcc-textDark transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                className="px-4 py-2 bg-hdlcc-accentRed text-white rounded hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Magazines Table */}
      <div className="bg-hdlcc-bgWhite rounded shadow-card border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-hdlcc-textDark">
                  Issue
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-hdlcc-textDark">
                  Published
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-hdlcc-textDark">
                  Details
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-hdlcc-textDark">
                  Uploaded
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-hdlcc-textDark">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {magazines.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-hdlcc-textMuted">
                    No magazines uploaded yet. Click &ldquo;Upload Magazine&rdquo; to add your first
                    issue.
                  </td>
                </tr>
              ) : (
                magazines.map(magazine => (
                  <tr
                    key={magazine.id}
                    className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      {editMode === magazine.id ? (
                        <input
                          type="text"
                          defaultValue={magazine.title}
                          onBlur={e => handleEdit(magazine.id, { title: e.target.value })}
                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <div>
                          <div className="font-semibold text-hdlcc-textDark">{magazine.title}</div>
                          <div className="text-sm text-hdlcc-textMuted">{magazine.quarter}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-hdlcc-textDark">
                      {new Date(magazine.publishedAt).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-4 py-3 text-sm text-hdlcc-textMuted">
                      {magazine.pages > 0 && `${magazine.pages} pages • `}
                      {magazine.pdfSize}
                    </td>
                    <td className="px-4 py-3 text-sm text-hdlcc-textMuted">
                      {magazine.uploadedBy}
                      <br />
                      {new Date(magazine.uploadedAt).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => window.open(`/magazine/${magazine.id}`, '_blank')}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="View"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => alert(`Downloading ${magazine.title}`)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Download"
                        >
                          <ArrowDownTrayIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setEditMode(editMode === magazine.id ? null : magazine.id)}
                          className="p-2 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(magazine)}
                          className="p-2 text-hdlcc-accentRed hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 p-4 rounded-lg">
        <p className="text-sm text-hdlcc-textDark">
          <strong className="text-blue-700">Magazine Management:</strong> Upload PDF files for
          quarterly magazines. Members can view and download these from their portal. Magazines are
          automatically sorted by publication date.
        </p>
      </div>
    </div>
  );
}

export default Magazine;
